import {
  applyCanvasMutation,
  type CanvasMutationHandler,
} from "./mutations.js";
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import * as Y from "yjs";
import { CanvasWidget } from "../widgets/CanvasWidget.js";
import { cn } from "../lib/utils.js";
import type {
  WidgetAskAIRequest,
  WidgetHostMetadata,
} from "../widgets/types.js";
import { useCanvasViewport } from "./hooks/useCanvasViewport.js";
import {
  awarenessColor,
  useCanvasWidgetActivityPeers,
  type CanvasAwarenessHandle,
  type CanvasAwarenessPeer,
} from "./awareness.js";
import { PresenceTag } from "./PresenceTag.js";
import type { CanvasWidgetLockState } from "./types.js";
import type { CanvasAwarenessActiveWidget } from "@playbookai/types";

interface WidgetLayerProps {
  readonly doc: Y.Doc;
  readonly widgetsMap: Y.Map<Y.Map<unknown>>;
  readonly canvasW: number;
  /** Lowest coordinate a widget may be dragged to. Default 0 — see StickyNoteLayer. */
  readonly canvasMinX?: number | undefined;
  readonly canvasMinY?: number | undefined;
  readonly canvasH: number;
  /**
   * Reserved band (canvas px) at the top of the canvas that a dragged widget
   * cannot enter — the drag clamp floor for Y. Default 0 (no reservation). A
   * canvas module with a fixed header band (e.g. SOLVE's phase-lane headers)
   * passes its header height so widgets never occlude it. Data-blind: the DS
   * canvas never hardcodes a module's header height.
   */
  readonly reservedTop?: number | undefined;
  readonly readOnly?: boolean | undefined;
  readonly resolveWidgetReadOnly?:
    | ((ymap: Y.Map<unknown>) => boolean)
    | undefined;
  readonly resolveWidgetVisibility?:
    | ((ymap: Y.Map<unknown>) => boolean)
    | undefined;
  readonly resolveWidgetLockState?:
    | ((ymap: Y.Map<unknown>) => CanvasWidgetLockState | null)
    | undefined;
  readonly awareness?: CanvasAwarenessHandle | null | undefined;
  /** Controlled selection (Canvas-owned) so deselect clears ring + awareness together. */
  readonly selected?: string | null | undefined;
  /**
   * The widget currently being pointed at by a reveal, outlined for a moment.
   * Separate from `selected` on purpose: showing somebody where a widget is
   * must not publish that they are working on it.
   */
  readonly highlighted?: string | null | undefined;
  readonly onWidgetSelect?: ((widgetId: string | null) => void) | undefined;
  readonly onWidgetActivity?:
    | ((activeWidget: CanvasAwarenessActiveWidget | null) => void)
    | undefined;
  readonly solveId?: string | undefined;
  readonly hostMetadata?: WidgetHostMetadata | undefined;
  readonly onWidgetRemove?:
    | ((widgetId: string, ymap: Y.Map<unknown>) => void)
    | undefined;
  readonly onWidgetAskAI?: ((request: WidgetAskAIRequest) => void) | undefined;
  readonly onCanvasMutation?: CanvasMutationHandler | undefined;
  readonly onWidgetDragEnd?:
    | ((widgetId: string, ymap: Y.Map<unknown>) => void)
    | undefined;
}

/**
 * Renders every widget in the `widgets` Y.Map as an absolutely-positioned
 * div inside the ReactFlow viewport transform. Widgets are NOT ReactFlow
 * nodes — they live outside the node graph, which is intentional (matches
 * the reference design; avoids nested-ReactFlow store conflicts when widgets
 * like Problem Tree later embed their own ReactFlow). See ADR-0003/0004.
 *
 * Drag is implemented locally via pointer events. dx/dy are translated into
 * viewport-space via the current ReactFlow zoom factor and written into the
 * widget's Y.Map x/y inside a `doc.transact(...)`.
 */
export function WidgetLayer({
  doc,
  widgetsMap,
  canvasW,
  canvasMinX = 0,
  canvasMinY = 0,
  canvasH,
  reservedTop = 0,
  readOnly,
  resolveWidgetReadOnly,
  resolveWidgetVisibility,
  resolveWidgetLockState,
  awareness,
  selected,
  highlighted,
  onWidgetSelect,
  onWidgetActivity,
  solveId,
  hostMetadata,
  onWidgetRemove,
  onWidgetAskAI,
  onWidgetDragEnd,
  onCanvasMutation,
}: WidgetLayerProps): ReactElement {
  const gestures = useRef(new Map<string, string>());
  const { x, y, zoom } = useCanvasViewport();
  const [, force] = useState(0);
  const peers = useCanvasWidgetActivityPeers(awareness);
  const localClientId = awareness?.clientID;
  const remotePeers = useMemo(
    () => peers.filter((peer) => peer.clientId !== localClientId),
    [localClientId, peers],
  );
  const activityByWidgetId = useMemo(
    () => remoteActivityByWidgetId(remotePeers),
    [remotePeers],
  );

  // Subscribe to widget set changes (add/remove); positions are tracked at
  // the widget level so the outer subscription doesn't fire on every drag.
  useEffect(() => {
    const observe = (): void => force((n) => n + 1);
    widgetsMap.observe(observe);
    return (): void => widgetsMap.unobserve(observe);
  }, [widgetsMap]);

  const entries: Array<[string, Y.Map<unknown>]> = [];
  widgetsMap.forEach((ymap, id) => {
    if (resolveWidgetVisibility?.(ymap) === false) return;
    entries.push([id, ymap]);
  });

  const onDragMove = (widgetId: string, dx: number, dy: number): void => {
    const ymap = widgetsMap.get(widgetId);
    if (!ymap) return;
    const curX = (ymap.get("x") as number) ?? 0;
    const curY = (ymap.get("y") as number) ?? 0;
    const w = (ymap.get("w") as number) ?? 200;
    const h = (ymap.get("h") as number) ?? 150;
    const nextX = clamp(
      curX + dx / zoom,
      canvasMinX,
      Math.max(canvasMinX, canvasW - w),
    );
    // `reservedTop` wins when a host reserves space at the top (it means "keep widgets
    // below this chrome"); otherwise the floor is canvasMinY, so a canvas with pan slack
    // above the origin can take widgets there. Defaults to 0 either way.
    const yFloor = reservedTop > 0 ? reservedTop : canvasMinY;
    const nextY = clamp(
      curY + dy / zoom,
      yFloor,
      Math.max(yFloor, canvasH - h),
    );
    applyCanvasMutation(
      onCanvasMutation,
      {
        target: "widget",
        action: "move",
        id: widgetId,
        entry: ymap,
        gestureId: gestures.current.get(widgetId),
      },
      () =>
        doc.transact(() => {
          ymap.set("x", nextX);
          ymap.set("y", nextY);
        }),
    );
  };

  return (
    <div
      data-testid="canvas-widget-layer"
      style={{
        position: "absolute",
        inset: 0,
        transformOrigin: "0 0",
        transform: `translate(${x}px, ${y}px) scale(${zoom})`,
        pointerEvents: "none",
      }}
    >
      {entries.map(([id, ymap], index) => (
        <WidgetWrapper
          key={id}
          ymap={ymap}
          widgetId={id}
          enterDelayMs={Math.min(
            index * ENTER_STAGGER_MS,
            ENTER_STAGGER_CAP_MS,
          )}
          doc={doc}
          readOnly={readOnly || resolveWidgetReadOnly?.(ymap) === true}
          lockState={resolveWidgetLockState?.(ymap) ?? null}
          selected={selected === id}
          highlighted={highlighted === id}
          remoteActivity={activityByWidgetId.get(id) ?? EMPTY_REMOTE_ACTIVITY}
          onSelect={(wid) => onWidgetSelect?.(wid)}
          onDragStart={(wid) => {
            gestures.current.set(wid, crypto.randomUUID());
            onWidgetActivity?.({ id: wid, mode: "dragging" });
          }}
          onDragMove={onDragMove}
          onDragEnd={(wid) => {
            gestures.current.delete(wid);
            const current = widgetsMap.get(wid);
            if (current) onWidgetDragEnd?.(wid, current);
            onWidgetActivity?.({ id: wid, mode: "selected" });
          }}
          onEditingChange={(wid, editing) =>
            onWidgetActivity?.(
              editing
                ? { id: wid, mode: "editing" }
                : selected === wid
                  ? { id: wid, mode: "selected" }
                  : null,
            )
          }
          onRemove={(wid) => {
            const current = widgetsMap.get(wid);
            if (current) onWidgetRemove?.(wid, current);
          }}
          onAskAI={onWidgetAskAI}
          solveId={solveId}
          hostMetadata={hostMetadata}
          onCanvasMutation={onCanvasMutation}
        />
      ))}
    </div>
  );
}

interface WidgetWrapperProps {
  readonly ymap: Y.Map<unknown>;
  readonly widgetId: string;
  readonly doc: Y.Doc;
  readonly readOnly?: boolean | undefined;
  readonly lockState?: CanvasWidgetLockState | null | undefined;
  readonly selected: boolean;
  readonly highlighted: boolean;
  /** Per-index stagger for the mount fade-up, capped so a big board never waits. */
  readonly enterDelayMs: number;
  readonly remoteActivity: readonly RemoteWidgetActivity[];
  readonly onSelect: (id: string) => void;
  readonly onDragStart: (id: string) => void;
  readonly onDragMove: (id: string, dx: number, dy: number) => void;
  readonly onDragEnd: (id: string) => void;
  readonly onEditingChange: (id: string, editing: boolean) => void;
  readonly onRemove?: ((id: string) => void) | undefined;
  readonly onAskAI?: ((request: WidgetAskAIRequest) => void) | undefined;
  readonly solveId?: string | undefined;
  readonly hostMetadata?: WidgetHostMetadata | undefined;
  readonly onCanvasMutation?: CanvasMutationHandler | undefined;
}

function WidgetWrapper({
  ymap,
  widgetId,
  doc,
  readOnly,
  lockState,
  selected,
  highlighted,
  enterDelayMs,
  remoteActivity,
  onSelect,
  onDragStart,
  onDragMove,
  onDragEnd,
  onEditingChange,
  onRemove,
  onAskAI,
  solveId,
  hostMetadata,
  onCanvasMutation,
}: WidgetWrapperProps): ReactElement {
  const [, force] = useState(0);
  useEffect(() => {
    const observe = (): void => force((n) => n + 1);
    ymap.observe(observe);
    return (): void => ymap.unobserve(observe);
  }, [ymap]);

  const x = (ymap.get("x") as number) ?? 0;
  const y = (ymap.get("y") as number) ?? 0;
  const w = (ymap.get("w") as number) ?? 220;
  const h = (ymap.get("h") as number) ?? 150;

  return (
    <div
      {...(highlighted ? { "data-canvas-revealed": "true" } : {})}
      className={cn(
        // Mount fade-up (ADR-0092): hand-rolled with `starting:` rather than
        // `.pb-motion-content-enter` because that unlayered class would own
        // transition-property and the reveal outline below could never fade.
        "transition-[opacity,translate,outline-color] duration-motion-overlay ease-motion-enter motion-reduce:transition-none starting:opacity-0 starting:translate-y-1",
        // OUTLINE, not a border or a ring with offset: an outline is drawn
        // outside the box and takes no layout, so pointing at a widget
        // cannot nudge it or its neighbours by a pixel. Always present and
        // transparent at rest so the reveal fades in and out.
        "rounded-lg outline outline-2 outline-offset-2",
        highlighted ? "outline-pb-cyan" : "outline-transparent",
      )}
      style={{
        // The delay list follows transition-property order: only the mount
        // fade-up is staggered; the reveal outline reacts immediately.
        transitionDelay: `${enterDelayMs}ms, ${enterDelayMs}ms, 0ms`,
        position: "absolute",
        left: x,
        top: y,
        width: w,
        height: h,
        pointerEvents: "auto",
      }}
    >
      <CanvasWidget
        widgetId={widgetId}
        ymap={ymap}
        doc={doc}
        readOnly={readOnly}
        lockState={lockState}
        selected={selected}
        onSelect={onSelect}
        onDragStart={onDragStart}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        onEditingChange={onEditingChange}
        onRemove={onRemove}
        onAskAI={onAskAI}
        solveId={solveId}
        hostMetadata={hostMetadata}
        onCanvasMutation={onCanvasMutation}
      />
      <RemoteWidgetActivityOverlay activity={remoteActivity} />
    </div>
  );
}

interface RemoteWidgetActivity {
  readonly name: string;
  readonly color: string;
  readonly mode: CanvasAwarenessActiveWidget["mode"];
}

const EMPTY_REMOTE_ACTIVITY: readonly RemoteWidgetActivity[] = [];

/** Mount stagger per widget, and the cap so a large board never waits. */
const ENTER_STAGGER_MS = 20;
const ENTER_STAGGER_CAP_MS = 200;

function remoteActivityByWidgetId(
  peers: readonly CanvasAwarenessPeer[],
): ReadonlyMap<string, readonly RemoteWidgetActivity[]> {
  const byWidgetId = new Map<string, RemoteWidgetActivity[]>();
  for (const peer of peers) {
    const activeWidget = activeWidgetFromPeer(peer);
    if (!activeWidget) continue;
    const activity = byWidgetId.get(activeWidget.id) ?? [];
    activity.push({
      name: peer.state.user?.name ?? "Collaborator",
      color: awarenessColor(peer.state.user?.color),
      mode: activeWidget.mode,
    });
    byWidgetId.set(activeWidget.id, activity);
  }
  return byWidgetId;
}

function activeWidgetFromPeer(
  peer: CanvasAwarenessPeer,
): CanvasAwarenessActiveWidget | null {
  if (peer.state.activeWidget !== undefined) return peer.state.activeWidget;
  return peer.state.selection
    ? { id: peer.state.selection, mode: "selected" }
    : null;
}

function RemoteWidgetActivityOverlay({
  activity,
}: {
  readonly activity: readonly RemoteWidgetActivity[];
}): ReactElement | null {
  if (activity.length === 0) return null;
  const [primary, ...rest] = activity;
  if (!primary) return null;
  /* DS keep (PR 3): a canvas presence frame painted in the peer's own
     awareness colour around a widget; no DS part draws it. */
  return (
    <div
      data-testid="remote-widget-activity"
      className="pointer-events-none absolute inset-0 z-30 rounded-lg border-2"
      style={{
        borderColor: primary.color,
        boxShadow: `0 0 0 3px color-mix(in srgb, ${primary.color} 18%, transparent)`,
      }}
    >
      <PresenceTag
        name={`${primary.name} ${activityLabel(primary.mode)}`}
        color={primary.color}
        overflowCount={rest.length}
        className="absolute -top-7 end-2 max-w-56"
      />
    </div>
  );
}

const ACTIVITY_LABEL: Record<CanvasAwarenessActiveWidget["mode"], string> = {
  selected: "selected",
  editing: "editing",
  dragging: "moving",
};

function activityLabel(mode: CanvasAwarenessActiveWidget["mode"]): string {
  return ACTIVITY_LABEL[mode];
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}
